import type { JobProgress } from "../lib/api";

/**
 * A sync's progress (SPEC §11.3): the worker's `done` of `total` steps and the step it is on.
 * Without a report yet (queued, or not claimed) it runs indeterminate.
 */
export function ProgressBar({
  progress,
  label,
  className = "",
}: {
  progress?: JobProgress | null;
  label?: string;
  className?: string;
}) {
  const pct =
    progress && progress.total > 0
      ? Math.min(100, Math.round((progress.done / progress.total) * 100))
      : null;
  const text = progress?.step ?? label ?? "Waiting for the sync service…";
  return (
    <div className={`space-y-1 ${className}`}>
      <div
        role="progressbar"
        aria-label={text}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct ?? undefined}
        className="relative h-1.5 w-full overflow-hidden rounded-full bg-[var(--line)]"
      >
        {pct === null ? (
          <div className="absolute inset-y-0 w-1/3 rounded-full bg-[var(--brand)] animate-[progress-slide_1.2s_ease-in-out_infinite]" />
        ) : (
          // At least a sliver, so a started sync never looks stuck at zero.
          <div
            className="h-full rounded-full bg-[var(--brand)] transition-[width] duration-300"
            style={{ width: `${Math.max(pct, 4)}%` }}
          />
        )}
      </div>
      <div className="flex justify-between gap-2 text-[11.5px] text-muted">
        <span className="truncate">{text}</span>
        {pct !== null && <span className="tnum shrink-0">{pct}%</span>}
      </div>
    </div>
  );
}
